import { createUserAssociation, deleteUserAssociation, getSinglePost, getSingleProfile, getUserAssociation, getUserAssociations, identity, uploadImage } from "deso-protocol";
import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { nanoid } from 'nanoid';
import { Alert, Button, ButtonGroup, Col, Collapse, Container, Dropdown, Form, FormControl, Image, InputGroup, Modal, Row, Spinner, ToggleButton } from "react-bootstrap";
import { localFees, systemKey } from "../..";
import { friendlyFormatDate, nodes } from "../../utils/helpers";
import { deso_graphql } from "../../utils/graphql";
import { DeSoIdentityContext } from "react-deso-protocol";
import ExchangeRatesContext from "../../contexts/ExchangeRatesContext";
import { handleLogin, UserDropdown } from "../../appLoader";
import { localCurrency } from "../../utils/currency";
import { useUserPreferences } from "../../contexts/UserPreferences";
import { QRCode } from 'react-qrcode-logo';
import { LinkQRCode } from "../../components/LinkQRCode";

export const OPENGRAPH_API_KEY = process.env.REACT_APP_OPENGRAPH_API_KEY; 

const bannedSlugs = [
  "",
  "get-started",
  "popular",
  "latest",
  "create",
  "myshares",
  "preferences",
  "about",
  "StarGeezer",
  "BeyondSocial",
  "pricing",
  "forum",
  "search",
  "login",
  "logout",
  "edit",
  "analytics",
  "analysis",
  "deso"
];

const generateShortLink = (postHashHex) => {
    // Create a unique ID (e.g., using the first 8 characters of a hash or nanoid for uniqueness)
    const uniqueId = nanoid(8);  // Using nanoid to generate an 8-character unique ID
    return uniqueId;
  };

const fetchMetadata = async (PostHashHex, nodeURI = "https://node.deso.org") => {
  try {
    const response = await getSinglePost({ PostHashHex });
    let isBlog = false;
    let isAudio = false;
    let isReply = false;

    if (response?.PostFound) {
      const post = response.PostFound;
      const profile = post.ProfileEntryResponse || {};
      const pubKey = profile.PublicKeyBase58Check;

      console.log("fetchMetadata PostFound:",post);

      let image =
        (post.PostExtraData?.CoverImage && post.PostExtraData.CoverImage !== '') && post.PostExtraData.CoverImage || // Check if CoverImage exists and is not blank
        (post.ImageURLs && post.ImageURLs[0]) && post.ImageURLs[0] ||
        profile.ExtraData?.FeaturedImageURL ||
        profile.ExtraData?.LargeProfilePicURL ||
        `${nodeURI}/api/v0/get-single-profile-picture/${pubKey}`;
      
      let twitterCardType = "summary";
      if (profile.ExtraData?.FeaturedImageURL || post.PostExtraData.CoverImage) {
        twitterCardType = "summary_large_image";
      }

      const maxDescriptionLength = 140;
      let description = "";

      if(post?.PostExtraData?.AudioURLs) { isAudio = true; }
      if(post?.ParentStakeID) { isReply = true; }

      if (post.PostExtraData?.BlogDeltaRtfFormat) {
        isBlog = true;
        try {
          const blogContent = JSON.parse(post.PostExtraData.BlogDeltaRtfFormat);
          console.log("Blog Content: ",blogContent);
          if (blogContent?.ops?.length > 0) {
            description = blogContent.ops
              .filter(block => typeof block.insert === "string") // Only keep text inserts
              .map(block => block.insert)
              .join(" ")
              .replace(/\n+/g, " ")
              .trim();
          }
        } catch (e) {
          console.error("Error parsing BlogDeltaRtfFormat:", e);
        }
      }

      if (!description) {
        description = post.Body.replace(/\n+/g, " ").trim(); // Fallback to Body
      }

      if (description.length > maxDescriptionLength) {
        let trimmed = description.substring(0, maxDescriptionLength);
      
        // Find the last space before the limit
        let lastSpace = trimmed.lastIndexOf(" ");
      
        if (lastSpace > 0) {
          // Trim to the last full word
          description = trimmed.substring(0, lastSpace).trim();
        } else {
          // No space found, find the next space after the limit
          let nextSpace = description.indexOf(" ", maxDescriptionLength);
          if (nextSpace > 0) {
            description = description.substring(0, nextSpace).trim();
          } else {
            // No space at all, use max limit
            description = trimmed.trim();
          }
        }
      
        description += "...";
      }

      const timestampSecs = post.TimestampNanos / 1e9;
      const datePublished = new Date(timestampSecs * 1000).toISOString();
      if(post?.PostExtraData?.BlogTitleSlug) {
        isBlog = true;
      }
      let title = "";
      if (post.PostExtraData?.Title) {
          title = post.PostExtraData.Title;
      } 
      else if (post.PostExtraData?.isMarkdown === "true") {
          const headingMatch = post.Body.match(/^#\s(.+)/); // Regex to match the first heading (e.g., # Heading)
          title = headingMatch ? headingMatch[1] : "";
      }

      const author = profile.ExtraData?.DisplayName || profile.Username || "Anonymous";

      if(post.IsNFT) {
        if (post?.PostExtraData?.["Collection Name"]) {
          if(!title) { title = "NFT - " + post?.PostExtraData?.["Collection Name"] + " "; }
        }
        if (post?.PostExtraData?.Edition) {
          if(!title) { title += post?.PostExtraData?.Edition + " "; }
        }
        if(!title) { title = "NFT by "+author; }
        description = "NFT by "+author+" - "+ (description ? ` - ` + description : ``);
      } else if ((!post.Body || post.Body.trim() === "") && post.ImageURLs && post.ImageURLs.length > 0) {
        if(!title) { title = `${post.ImageURLs.length > 1 ? 'Images' : 'Image'} by ${author}`; }
        else { description = "Image by "+author+ (description ? ` - ` + description : ``); }
      } else if ((!post.Body || post.Body.trim() === "") && post.VideoURLs && post.VideoURLs.length > 0) {
        if(!title) { title = `${post.VideoURLs.length > 1 ? 'Videos' : 'Video'} by ${author}`; }
        else { description = "Video by "+ author + (description ? ` - ` + description : ``); }
      } else if (isAudio) {
        if(!title) { title = `Audio by ${author}`; }
        else { description = "Audio by "+ author + (description ? ` - ` + description : ``); }
      } else if (isBlog) {
        if(!title) { title = `Article by ${author}`; }
        else { description = "Article by "+ author + (description ? ` - ` + description : ``); }
      } else if (isReply) {
        if(!title) { title = `Reply by ${author}`; }
        else { description = "Reply by "+ author + (description ? ` - ` + description : ``); }
      } else {
        if(!title) { title = `Post by ${author}`; }
        else { description = "Post by "+ author + (description ? ` - ` + description : ``); }
      }

      const metadata = {
          title,
          description,
          isBlog: isBlog ? "true" : "false",
          isMarkdown: post?.PostExtraData?.isMarkdown === "true" ? "true" : "false",
          isAudio: post?.PostExtraData?.AudioURLs ? "true" : "false",
          image,
          username: profile.ExtraData?.DisplayName || profile.Username || "Anonymous",
          datePublished,
          PostHashHex: post.PostHashHex,
          type: "article",
          author: profile?.ExtraData?.DisplayName || profile.Username || "Anonymous",
          cardType: twitterCardType,
          blogSlug: isBlog ? post?.PostExtraData?.BlogTitleSlug : "",
        };

      console.log("Post, Meta:",post, metadata);
      return metadata;
    }
  } catch (error) {
    console.error("Error fetching metadata:", error);
    return null;
  }
};

const getExternalMetadata = async (url) => {
  console.log("getExternalMetadata",url);
  try {
    const apiUrl = `https://opengraph.io/api/1.1/site/${encodeURIComponent(url)}?app_id=${OPENGRAPH_API_KEY}&cache_ok=true&full_render=false`;
    const response = await fetch(apiUrl);
    console.log("externalMetaRequest:",url,apiUrl,response);

    if (!response.ok) throw new Error("Failed to fetch metadata");

    const data = await response.json();
    const ogData = data?.hybridGraph || {}; // Use hybridGraph for best results

    // Extracting relevant fields
    const metadata = {
      title: ogData.title || "",
      description: ogData.description || "",
      isBlog: "false", // Not determined by OpenGraph, can be modified based on URL structure
      isMarkdown: "false",
      isAudio: ogData.type === "music" ? "true" : "false",
      image: ogData.image || "",
      username: ogData.site_name || "Anonymous", // Use site name if available
      datePublished: ogData.published_time || new Date().toISOString(),
      PostHashHex: "", // External sources won't have this, leave blank or handle differently
      type: ogData.type || "article",
      author: ogData.author || ogData.site_name || "Anonymous",
      cardType: ogData.type === "video" ? "summary_large_image" : "summary",
      blogSlug: "", // Not relevant for external metadata
    };
    return metadata;
  } catch (error) {
    //console.error("Error fetching external metadata:", error);
    return { error: error };
  }
};


const generateSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-"); // Replace spaces with hyphens
};
  
export const ShareLink = ({ url, preview = false, suppliedPostHashHex, association }) => {
  const { currentUser, alternateUsers, isLoading } = useContext(DeSoIdentityContext);
  const { preferences } = useUserPreferences();
  const mode = association?.associationId ? true : false;
  const [inputUrl, setInputUrl] = useState(association?.extraData?.PostHashHex || association?.extraData?.externalURL || url || "");
  const [postHashHex, setPostHashHex] = useState(suppliedPostHashHex || "");
  const [shortLink, setShortLink] = useState(`https://w3ly.io/${association?.associationValue}` || "");
  const [copied, setCopied] = useState(false);
  const [checkingMetadata, setCheckingMetadata] = useState(false);
  const [metadata, setMetadata] = useState(association?.extraData || null);
  const [shortLinkGenerated, setShortLinkGenerated] = useState(association ? true : false);
  const [showPreview, setShowPreview] = useState('twitter-x');
  const [editMetadata, setEditMetadata] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [qrAccentColor, setQrAccentColor] = useState("#FFFFFF");
  const [qrColor, setQrColor] = useState("#000000");

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const [formData, setFormData] = useState({
    linkType: association?.extraData?.linkType || 1,
  });
  const { exchangeRates } = useContext(ExchangeRatesContext);
  let debounceTimer = null;

  let feeUSD = 0.25 * formData.linkType;
  let feeInDESONanos = 25000000 * formData.linkType;
  let updateFeeUSD = 0.05;
  let updateFeeInDESONanos = 5000000;
  
  if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
    feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
    updateFeeInDESONanos = Math.round((updateFeeUSD / exchangeRates["USD"]["rate"]) * 1e9);
  }

  useEffect(() => {
    feeUSD = 0.25 * formData.linkType;
    if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
      feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
    }
    console.log(formData,feeUSD,feeInDESONanos);
  }, [formData.linkType, exchangeRates])

  useEffect(() => {
    console.log("formData",formData,"Metadata",metadata);
  }, [formData,metadata]);

  const resetMetadata = async () => {
    if(mode==="edit") {
      setMetadata(association.extraData);
    } else {
      const metadata = await fetchMetadata(postHashHex);
      setMetadata(metadata);
    }
  }

  const postHashRegex = /\/(?:posts|nft|post)\/([a-fA-F0-9]{64})/;
  const blogRegex = /\/u\/([^/]+)\/blog\/([^/]+)/; // Captures username & blog-slug
  
  const fetchPreviewData = async (url) => {
    if (!url) {
      setPostHashHex("");
      setMetadata(null);
      return;
    }
  
    const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,}))([\/\?%#&=0-9a-zA-Z_-]*)$/;

    // Regex for PostHashHex (64-character hexadecimal)
    const postHashPattern = /^[0-9a-fA-F]{64}$/;
  
    let isValidInput = urlPattern.test(url) || false;
    let extractedHash = "";

    if(postHashPattern.test(url)) {
      isValidInput = true;
      extractedHash = url;
    }
  
    if (!isValidInput) {
      console.error("Invalid URL:", url);
      setFormData((prev) => ({
        ...prev,
        invalidURL: 'Please enter a valid URL',
      }));
      setMetadata(null);
      return;
    }
  
    setFormData((prev) => ({
      ...prev,
      invalidURL: false,
    }));

    const postMatch = url.match(postHashRegex);
    if (postMatch && !extractedHash) {
      extractedHash = postMatch[1];
    } else if (!extractedHash) {
      console.log("URL is not a PostHashHex, check for blog...");
      const blogMatch = url.match(blogRegex);
      if (blogMatch) {
        const username = blogMatch[1];
        const blogSlug = blogMatch[2];
        console.log("Blog: Username, Slug:", username, blogSlug);
      
        try {
          const profile = await getSingleProfile({ Username: username });
          console.log(profile, profile?.Profile?.ExtraData?.BlogSlugMap);
          extractedHash = null;
          const blogSlugMap = profile?.Profile?.ExtraData?.BlogSlugMap;
      
          if (blogSlugMap) {
            try {
              const parsedSlugMap = JSON.parse(blogSlugMap);
              extractedHash = parsedSlugMap[blogSlug] || null;
            } catch (error) {
              console.error("Error parsing BlogSlugMap JSON:", error);
            }
          }
      
          console.log("Extracted PostHashHex:", extractedHash);
          
          if (!extractedHash) {
            console.warn("No matching post found for slug:", blogSlug);
            setMetadata(null);
            setCheckingMetadata(false);
            return;
          }
      
          // Continue processing with extractedHash
      
        } catch (error) {
          console.error("Error fetching post hash:", error);
          setMetadata(null);
          setCheckingMetadata(false);
          return;
        }
      }
      
    }
  
    if (extractedHash) {
      setPostHashHex(extractedHash);
      if(!metadata?.customised) {
        const metadata = await fetchMetadata(extractedHash);
        console.log("Generated metadata:", metadata);
        setMetadata(metadata);
        setCheckingMetadata(false);
      }
      setFormData((prev) => ({
        ...prev,
        externalURL: "",
      }));
    } else {
      // External URL
      console.log("Non deso link");
      let updatedFormdata = {
        externalURL: url,
      };
      
      // Reset post hash
      setPostHashHex("");
      
      if (!metadata?.customised) {
        console.log("Not customised so make a call for the meta");
      
        if (!currentUser) {
          updatedFormdata = {
            ...updatedFormdata,
            userError: "Sorry, but you must be logged in to preview external links",
            mustLogin: true,
          };
          setMetadata(null);
        } else {
          try {
            const externalMeta = await getExternalMetadata(url);
            if(externalMeta.error) {
              updatedFormdata = {
                ...updatedFormdata,
                metaError: "Sorry, unable to retrieve metadata for this url, please enter manually",
                mustLogin: false,
                error: '',
              };
              setMetadata({});
            } else {
              setMetadata(externalMeta);
              updatedFormdata = {
                ...updatedFormdata,
                mustLogin: false,
                userError: '',
                error: '',
              };
            }  
          } catch (error) {
            console.log("Error",error);
          }
        }
        setCheckingMetadata(false);
      } else {
        console.log("Customised, so user can configure manually");
      }
      
      // Merge with existing form data while keeping old values
      setFormData((prev) => ({
        ...prev,
        ...updatedFormdata,
      }));
      
    }
  };
  
  // Run on mount if preview is enabled
  useEffect(() => {
    if (mode !== "edit" && preview && url && !metadata?.customised) {
      fetchPreviewData(url);
    }
  }, [preview, url]);

  useEffect(() => {
    if(formData.linkType > 2) {
      if (formData.customText) {
        const slug = generateSlug(formData.customText);
        console.log("Checking custom url:",formData.customText,slug);
        setCheckingSlug(true);
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          const exists = await checkSlugAvailability(slug);
          setCheckingSlug(false);
  
          setFormData((prev) => ({
            ...prev,
            customSlug: slug,
            slugError: exists ? "Slug is already taken. Please choose another." : "",
          }));
        }, 500); // 500ms debounce
      } else {
        setFormData((prev) => ({
          ...prev,
          customSlug: "",
          slugError: "",
        }));
      }
    }
    return () => clearTimeout(debounceTimer); // Cleanup on unmount or change
  }, [formData.customText, formData.linkType]);
  
  const handleInputChange = (e) => {
    setInputUrl( e.target.value);
    setCheckingMetadata(true);
  };

  useEffect(() => {
    if (!inputUrl) {
      setMetadata(null);
      return;
    }
  
    const handler = setTimeout(() => {
      fetchPreviewData(inputUrl);
    }, 1000);
  
    return () => clearTimeout(handler);
  }, [inputUrl]);
  
  const checkSlugAvailability = async (slug) => {
    if (!slug) return false; // No need to check empty slug
  
    let associationValue = slug;
    if(formData.linkType === 2) {
      associationValue = `${currentUser.PublicKeyBase58Check}/${slug}`;
    }

    if (bannedSlugs.includes(associationValue)) {
      console.log("This slug is banned.");
      return true; // Banned slug, assume it's unavailable
    }

    try {
      const variables = {
        first: 1, // We only need to check if one exists
        offset: 0,
        condition: {
          associationType: "SHORTLINK",
          associationValue: associationValue,
        },
      };
  
      const data = await deso_graphql({
        operationName: "CheckSlug",
        variables,
        query: `
          query CheckSlug($first: Int, $offset: Int, $condition: UserAssociationCondition) {
            userAssociations(first: $first, offset: $offset, condition: $condition) {
              nodes {
                associationValue
              }
              totalCount
            }
          }`,
      });
  
      console.log("Checking slug result:",variables,data);
      return data?.data?.userAssociations?.totalCount > 0; // True if slug already exists
    } catch (error) {
      console.error("Error checking slug availability:", error);
      return true; // Assume unavailable on error
    }
  };
  

  const createShortLink = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      let guid = nanoid(8);
      let shortId = guid;

      const associationRequest = {
        AppPublicKeyBase58Check: systemKey,
        TargetUserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationType: "SHORTLINK",
      };

      if(association) {
        // editing an existing association
        associationRequest.AssociationValue = association.associationValue;
        associationRequest.ExtraData = {
          ...association.extraData,
          ...metadata,
        }
        associationRequest.TransactionFees = [localFees()];
      } else {
        // Step 1: Generate a unique short ID (e.g., using nanoid)
        switch(formData.linkType) {
          case 2:
            shortId = currentUser.ProfileEntryResponse.Username + `/` +guid;
            break;
          case 3:
            shortId = currentUser.ProfileEntryResponse.Username + `/` +formData.customSlug;
            break;
          case 4:
            shortId = formData.customSlug
            break;
        }
        associationRequest.AssociationValue = shortId;
        associationRequest.ExtraData = {
          PostHashHex: postHashHex || "",
          shortLink: shortId,
          linkType: formData?.linkType.toString() || "",
          customSlug: formData?.customSlug || "",
          externalURL: formData?.externalURL || "",
          ...metadata,
        };
        associationRequest.TransactionFees = [localFees(null,feeInDESONanos)];
      }
      
      console.log("Create Association:",associationRequest);
      const associationResponse = await createUserAssociation(associationRequest);
      console.log("Association:",associationRequest,associationResponse);

      // Step 3: Check if the creation was successful and update the UI
      if (associationResponse?.submittedTransactionResponse) {
        setShortLink(`https://w3ly.io/${shortId}`);
        setShortLinkGenerated(true);
        setSuccessMessage("Short Link Created");
      } else {
        console.error("Failed to create short link on blockchain:", associationResponse?.error);
        setErrorMessage(`Unable to ${association ? `update` : `create`} Short Link, please try again`);
      }
    } catch (error) {
      console.error("Error creating short link:", error);
      setErrorMessage(`Unable to ${association ? `update` : `create`} Short Link, please try again`);
    }
  };

  const deleteShortLink = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    if(!association) return;

    try {
      const associationRequest = {
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationID: association.associationId,
        TransactionFees: [localFees()],
      };
      
      console.log("Delete Association:",associationRequest);
      const associationResponse = await deleteUserAssociation(associationRequest);
      console.log("Association:",associationRequest,associationResponse);

      // Step 3: Check if the creation was successful and update the UI
      if (associationResponse?.submittedTransactionResponse) {
        setShortLink(null);
        setShortLinkGenerated(false);
        setSuccessMessage("Short Link Deleted")
      } else {
        console.error("Failed to delete short link on blockchain:", associationResponse?.error);
        setErrorMessage("Unable to delete Short Link, please try again");
      }
    } catch (error) {
      console.error("Error deleting short link:", error);
      setErrorMessage("Unable to delete Short Link, please try again");
    }
  };

  const copyToClipboard = async () => {
    if (!shortLink) return;
    try {
      await navigator.clipboard.writeText(shortLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: metadata.title,
        url: shortLink,
      }).catch((error) => {
        console.error('Error sharing:', error);
      });
    } else {
      alert('Sorry, sharing is not supported on this device - please copy the link instead.');
    }
  };

  const previews = getPlatformPreviews(metadata);
  const platformStyles = {
    facebook: { selected: "text-primary", iconSelected: 'bg-light' },
    'twitter-x': { selected: "bg-body text-light", iconSelected: '' },
    linkedin: { selected: "bg-light text-primary", iconSelected: '' },
    instagram: { selected: "bg-danger text-light", iconSelected: '' },
    youtube: { selected: "bg-danger text-light", iconSelected: '' },
    tiktok: { selected: "bg-body text-light", iconSelected: '' },
    pinterest: { selected: "bg-danger text-light", iconSelected: '' },
    telegram: { selected: "telegram", iconSelected: 'bg-light' },
  };
  
  return (
      <div className="card p-3">
        {preview ? (
          <h6 className="mb-3">Example</h6>
        ) : (
          <div>
            {mode === "edit" ? (
              <h6>Edit your link</h6>
            ) : (
              <h6>Paste a Link to Generate a Short URL</h6>
            )}
            <input
              type="text"
              value={inputUrl}
              onChange={handleInputChange}
              placeholder="Enter Valid URL or DESO PostHashHex..."
              className={`form-control ${formData.invalidURL ? `is-invalid` : ``}`}
              disabled={preview || association}
            />
            {formData.invalidURL && (<div className="text-danger mt-2">{formData.invalidURL}</div>)}
            {association && (<div className="text-muted small mt-2">You cannot change the destination of an existing link</div> )}
          </div>
        )}
        {metadata && (
          <>
            {formData?.metaError && !metadata?.customised && (
              <div className="text-danger mt-2">{formData?.metaError}</div>
            )}
            {previews && (
              <div className="mt-3 text-center">
                <div className="d-flex flex-row align-items-center justify-content-between mb-3">
                  <span className="ps-1">Preview</span>
                  <InputGroup className="w-100 justify-content-end">
                  {Object.keys(previews)
                    .filter((key) => key !== "appPreview")
                    .map((key) => (
                      <span
                        key={key}
                        className={`btn border-secondary ${
                          showPreview === key
                            ? `text-body`
                            : "text-muted bg-transparent border-opacity-50 opacity-50"
                        }`}
                        onClick={() => setShowPreview(key)}
                      >
                        {showPreview === `${key}` && platformStyles[key]?.iconSelected ? (
                          <span className={`${platformStyles[key]?.iconSelected} filled-icon`}>
                            <i className={`bi bi-${key}`}></i>
                          </span>
                        ) : (
                          <i className={`bi bi-${key}`}></i>
                        )}
                      </span>
                    ))}
                  </InputGroup>
                </div>
                {/* Display the selected preview */}
                <Row>
                  {Object.keys(previews)
                    .filter((key) => key !== "appPreview")
                    .map((key) => (
                      <Col key={key} xs={12} className={`collapse ${showPreview === key ? "show" : ""}`}>
                        {previews[key]}
                      </Col>
                  ))}

                </Row>
              </div>
            )}

            {!shortLinkGenerated && (
              <>
                {editMetadata && (
                  <MetadataForm currentUser={currentUser} metadata={metadata} setMetadata={setMetadata} resetMetadata={resetMetadata} />
                )}

                <div className="d-flex justify-content-center">
                  <Button
                    variant={editMetadata ? `success` : `secondary`}
                    size={'sm'}
                    className={`mb-3`}
                    onClick={() => setEditMetadata(!editMetadata)}
                  >
                    {editMetadata ? <><i className="bi bi-check-circle-fill me-2"></i>Finished Editing Metadata</> : <><i className="bi bi-pencil-square me-2"></i>Edit Metadata</>}
                  </Button>
                </div>
              </>
            )}


            {!preview && !editMetadata && (
              <>
                {!shortLinkGenerated && (
                  <LinkForm currentUser={currentUser} formData={formData} setFormData={setFormData} />
                )}

                {successMessage && (
                  <Alert variant="success" className="py-1 px-2 my-2">
                    {successMessage}
                  </Alert>
                )}

                {errorMessage && (
                  <Alert variant="danger" className="py-1 px-2 my-2">
                    {errorMessage}
                  </Alert>
                )}

                {!shortLinkGenerated && (formData.linkType < 3 || ((formData?.linkType === 3 || formData?.linkType === 4) && formData.customSlug && !formData?.slugError)) && (
                  <div className="my-2">
                    <Button onClick={createShortLink}>Create Short Link ({localCurrency(feeInDESONanos/1e9,preferences,exchangeRates)})</Button>
                  </div>
                )}

                {shortLinkGenerated && association && (
                  <div className="mb-3 d-flex flex-row justify-content-evenly align-items-center">
                    {JSON.stringify(association.extraData) !== JSON.stringify(metadata) && (
                      <Button onClick={createShortLink}>Save Changes</Button>
                    )}
                    <Button variant={'danger'} onClick={deleteShortLink}><i className="bi bi-trash3-fill me-2"></i>Delete Short Link</Button>
                  </div>
                )}

                {shortLinkGenerated && (
                  <div className="rounded bg-secondary border-secondary bg-opacity-25 p-2">
                    <h4 className="mt-2"><i className="bi bi-check-circle-fill text-success me-2"></i>Link {association ? `Details` : `Generated`}</h4>
                    <InputGroup className="mt-3">
                      <FormControl
                        type="text"
                        value={shortLink}
                        readOnly
                        aria-describedby="basic-addon2"
                      />
                      <Button 
                        onClick={copyToClipboard} 
                        variant={copied ? `success` : `primary`}>
                        <i className={`bi bi-${copied ? `check2-square` : `copy`}`}></i>
                      </Button>
                      <Button 
                        onClick={handleShare} 
                        variant="info">
                        <i className="bi bi-share-fill"></i>
                      </Button>
                    </InputGroup>
                    <LinkQRCode data={shortLink} currentUser={currentUser} />
                    <div className="mt-2 text-muted small">
                      <i className="bi bi-info-circle-fill me-1"></i>Access your generated links at any time from the <Link to={`/myshares`} className="text-body fst-italic">My Shares</Link> page.
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };


  export const RedirectPage = () => {
    const { currentUser, alternateUsers, isLoading } = useContext(DeSoIdentityContext);
    const location = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();

    const pathParts = location.pathname.replace(/^\/+/, "").split("/");
    let username = null;
    let extractedId = id || pathParts[pathParts.length - 1];
    if (pathParts.length > 1 && pathParts[1]) {
      username = pathParts[0];
      extractedId = pathParts[1];
    } else {
      extractedId = pathParts[0];
    }
    console.log("Slug info: username:" + username + " extractedId:"+extractedId);
    
    // States for async loading and redirection
    const [association, setAssociation] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [goToDestination, setGoToDestination] = useState(false);
    const [showModal, setShowModal] = useState(false);
  
    const [availableFrontends, setAvailableFrontends] = useState([]);
    const [updatedSettings, setUpdatedSettings] = useState(false);

    const [redirectAutomatically, setRedirectAutomatically] = useState(false);
    const { exchangeRates } = useContext(ExchangeRatesContext);
    const feeUSD = 0.01;
    let feeInDESONanos = 25000000;
    if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
      feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
    }
    console.log("Fee in DESO Nanos:",feeInDESONanos);

    const [formData, setFormData] = useState({
        selected: "",
        redirectAutomatically: false,
    });

    useEffect(() => {
      if(redirectUrl && redirectAutomatically) {
        setGoToDestination(true);
      }

      if (redirectUrl && goToDestination) {
        sendStats(association, 'followed', currentUser || null);
        window.location.href = redirectUrl;
      }
    }, [redirectUrl, goToDestination, redirectAutomatically]);

    // Fetch user preferences from API and fall back to localStorage if unavailable
    const fetchUserPreferences = async () => {
      let preferencesDetected = false;
      if(currentUser) {
        try {
          const data = await deso_graphql({
              "operationName": "UserAssociations",
              "variables": {
                  "first": 1,
                  "condition": {
                      "associationType": "W3LY_SETTINGS",
                      "associationValue": "W3LY_SETTINGS",
                      "transactorPkid": currentUser.PublicKeyBase58Check,
                  }
              },
              query: `
                  query UserAssociations($first: Int, $condition: UserAssociationCondition) {
                      userAssociations(first: $first, condition: $condition) {
                          nodes {
                              extraData
                              transactor {
                                  username
                                  extraData
                                  publicKey
                              }
                          }
                      }
                  }`
          });
          
          if (data?.data?.userAssociations?.nodes[0]?.extraData) {
              const userPrefs = data.data.userAssociations.nodes[0].extraData;
              console.log("Retreived User Preferences Association",userPrefs);
              setFormData(prev => ({
                  ...prev,
                  selected: userPrefs.preferredFrontend || prev.selected,
                  redirectAutomatically: userPrefs.redirectAutomatically === "true" || prev.redirectAutomatically
              }));
              preferencesDetected = true;
              setRedirectAutomatically(userPrefs.redirectAutomatically === "true" || false);
          } 
        } catch (error) {
          console.log("Error Fetching User Preferences from Chain:",error);
        }
      }
      if(!preferencesDetected) {
          // Fallback to localStorage
          console.log("Retreived LocalStorage Preferences",localStorage);
          setFormData(prev => ({
              ...prev,
              selected: localStorage.getItem("preferredFrontend") || "",
              redirectAutomatically: localStorage.getItem("redirectAutomatically") === "true" || false
          }));
          setRedirectAutomatically(localStorage.getItem("redirectAutomatically")  === "true" || false);
      }
    };

    // Fetch association data
    const fetchAssociationData = async () => {
      try {
        console.log("Lookup Association:", { username, extractedId });
    
        let variables;


        
        if (username) {
          // Lookup based on username + slug
          variables = {
            first: 1,
            condition: {
              associationType: "SHORTLINK",
              associationValue: `${username}/${extractedId}`
            },
          };
        } else {
          // Lookup based on extractedId alone
          variables = {
            first: 1,
            condition: {
              associationType: "SHORTLINK",
              associationValue: extractedId,
            }
          };
        }
    
        const data = await deso_graphql({
          operationName: "UserAssociations",
          variables,
          query: `
            query UserAssociations($first: Int, $condition: UserAssociationCondition, $filter: UserAssociationFilter) {
              userAssociations(first: $first, condition: $condition, filter: $filter) {
                nodes {
                  associationId
                  associationValue
                  extraData
                  transactor {
                    username
                    extraData
                    publicKey
                  }
                }
              }
            }`
        });
    
        console.log("QL:",variables,data);
        setAssociation(data?.data?.userAssociations?.nodes[0] || null);
        setMetadata(data?.data?.userAssociations?.nodes[0]?.extraData || {});
        if(data?.data?.userAssociations?.nodes[0]?.extraData.externalURL) {
          setRedirectUrl(data?.data?.userAssociations?.nodes[0]?.extraData.externalURL);
          setGoToDestination(true);
          // send data to backend
        }
      } catch (error) {
        console.error("Error fetching association data:", error);
      }
    };
    

    useEffect(() => {
      if(!isLoading) {
        fetchUserPreferences();
        fetchAssociationData();
      }
    }, [isLoading, id, currentUser]);

    const [initialStatsLogged, setInitialStatsLogged] = useState(false);
    useEffect(() => {
      if(!isLoading && association && !initialStatsLogged) {
        sendStats(association, 'retrieved', currentUser)
        setInitialStatsLogged(true);
      }
    }, [isLoading, association]);

    useEffect(() => {
        console.log(association, metadata);
        if (metadata) {
          if(metadata.externalURL) {
            setRedirectUrl(metadata.externalURL);
          } else {
            if (formData.selected && nodes[formData.selected]?.Active && nodes[formData.selected].FrontendURL) {
                const frontendUrl = nodes[formData.selected]?.FrontendURL;
                if (frontendUrl.includes("focus.xyz")) {
                    setRedirectUrl(`${frontendUrl}/post/${metadata.PostHashHex}`);
                } else {
                    setRedirectUrl(`${frontendUrl}/posts/${metadata.PostHashHex}`);
                }
            } else {
                setRedirectUrl(null);
            }
          }
        }
    }, [association, metadata, formData.selected]);

    useEffect(() => {
        const activeNodes = Object.keys(nodes)
            .filter((key) => nodes[key].Active && nodes[key].FrontendURL)
            .map((key) => ({
                ...nodes[key],
                id: key,
            }))
            .reverse();
        setAvailableFrontends(activeNodes);
    }, []);

    // Save preferences to both localStorage and backend
    const savePreference = async () => {
        let changes = false;

        // Save to localStorage
        if (formData.selected !== localStorage?.getItem("preferredFrontend")) { 
            localStorage.setItem("preferredFrontend", formData.selected); 
            changes = true; 
        }
        if (formData.redirectAutomatically.toString() !== localStorage?.getItem("redirectAutomatically")) { 
            localStorage.setItem("redirectAutomatically", formData.redirectAutomatically.toString()); 
            changes = true; 
        }

        if (changes) {
            try {
                const associationRequest = {
                    AppPublicKeyBase58Check: systemKey,
                    TargetUserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
                    TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
                    AssociationType: "W3LY_SETTINGS",
                    AssociationValue: "W3LY_SETTINGS",
                    ExtraData: {
                        redirectAutomatically: formData.redirectAutomatically.toString(),
                        preferredFrontend: formData.selected,
                    },
                    TransactionFees: [localFees()],
                };
                await createUserAssociation(associationRequest);
            } catch (error) {
                console.log("Error saving preferences to chain", error);
            }
        }

        setUpdatedSettings(true);
        setRedirectAutomatically(formData.redirectAutomatically || false);
        setGoToDestination(true);
        setShowModal(false);
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        savePreference();
    };

      return (
        <>
          {!association && (
            <div className="vh-100 w-100 d-flex align-items-center justify-content-center">
              <Spinner />
            </div>
          )}
          <div className={`modal fade ${association ? `d-block show` : `d-none`}`} tabIndex="-1" style={{ zIndex: 1 }}>
              <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 10, pointerEvents: 'auto' }}>
                  <div className="modal-body bg-secondary bg-opacity-75 rounded-5 text-center" style={{ zIndex: 20, pointerEvents: 'auto' }}>
                    
                      
                      {availableFrontends.length === 0 ? (
                          <Alert variant="warning">No active frontends available.</Alert>
                      ) : (
                          <div style={{ zIndex: 500 }}>
                            <LinkPreview metadata={metadata} />
                            {goToDestination ? (
                              <div className="w-100 d-flex flex-column align-items-center justify-content-center py-5">
                                <Spinner />
                                ...redirecting to {metadata?.externalURL ? metadata?.externalURL : nodes[formData.selected]?.Name}...
                              </div>
                            ) : (
                              <>
                                <div className="w-100 d-flex flex-row justify-content-evenly align-items-center mt-1">
                                  <div>
                                    <UserDropdown />
                                  </div>

                                  <div className="">
                                      <Form.Label className="fw-bold visually-hidden">Auto-redirect?</Form.Label>
                                      <div className="form-switch">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={!formData.redirectAutomatically}
                                          onChange={() => setFormData(prev => ({ ...prev, redirectAutomatically: !prev.redirectAutomatically }))}
                                        />
                                        <label className="ps-2 small text-muted text-nowrap form-check-label">
                                            always prompt
                                        </label>
                                      </div>
                                  </div>
                                </div>

                                {/*
                                <div className="d-flex flex-column align-items-center justify-content-center px-3">
                                  <Button variant={goToDestination ? `outline-success` : `primary`} disabled={goToDestination} onClick={savePreference} className="mt-2" style={{ pointerEvents: 'auto' }}>
                                    {goToDestination ? <Spinner size="sm" /> : `Visit Link`}
                                  </Button>
                                </div>
                                */}

                                <Form style={{ pointerEvents: 'auto' }}>
                                  <div className="py-1 fw-bold">Open with...</div>
                                    <Row className="d-flex justify-content-center text-center px-3">
                                        {availableFrontends.map((node) => (
                                            <Col
                                                xs={4} sm={4} md={3}
                                                key={node.id}
                                                className={`d-flex flex-column align-items-center justify-content-center rounded-4 py-1 border ${formData.selected === node.id ? 'bg-secondary border-secondary bg-gradient bg-opacity-75 border-primary fw-bold' : 'border-opacity-0'}`}
                                                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                                                onClick={() => handleChange('selected', node.id)}
                                            >
                                                <Image 
                                                    src={`${node.URL ? node.URL : `https://node.deso.org`}/api/v0/get-single-profile-picture/${node.EntryPublicKeyBase58Check}`}
                                                    alt={node.name}
                                                    roundedCircle
                                                    className="img-fluid object-fit-cover px-3"
                                                    style={{ maxWidth: "75px" }}
                                                />
                                                <div className="mt-1 small">{node.Name}</div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Form>
                              </>  
                            )}
                          <div className="text-muted mt-1 px-3">
                            <span className="small"><i className="bi bi-info-circle-fill me-2"></i>create links, QR codes, analyse performance and set your preferences at</span><br/>
                            <Link to={"/"} className="text-body text-decoration-none text-nowrap fw-bold">
                              <span className="fw-bold text-info">
                                <i className="bi bi-share-fill me-2"></i>w3ly.io
                              </span>
                              <span className="fw-light ps-1">web 3-ly</span>
                            </Link>
                          </div>
                        </div>
                      )}
                  </div>
              </div>
          </div>
      </>
    );
};

export const LinkPreview = ({currentUser, association = null, metadata = null, setRefreshRequired}) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  if(!association && !metadata) return;
  const previewMeta = association?.extraData || metadata;
  return (
    <div className="w-100 h-100 position-relative">
      <Link to={association ? `/${association?.associationValue}` : undefined}>
      {previewMeta.cardType === "summary_large_image" ? (
        <div className="position-relative bg-dark border border-muted rounded-3 overflow-hidden">
          <div
            className="ratio ratio-21x9 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${previewMeta.image})`,
              backgroundColor: "#333", // fallback in case the image doesn't load
            }}
          >
            <img
              className="d-block w-100 h-100 position-absolute top-0 object-fit-cover"
              src={previewMeta.image}
              alt="Preview"
            />
          </div>
          <div className="position-absolute start-0 bottom-0 text-light text-start lh-1 m-2 p-2 rounded bg-black bg-opacity-75">
            <div
              className="my-1"
            >
              {previewMeta.title}
            </div>
            <div
              className="socialcarddescription small lh-1"
            >
              {previewMeta.description}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-100 h-100 d-flex flex-row align-items-center position-relative bg-dark border border-muted rounded-3 overflow-hidden">
          {previewMeta.image ? (
            <Image 
              src={previewMeta.image} 
              className="h-100 object-fit-cover"
              style={{ maxWidth: "125px", minWidth: "125px", minHeight: "125px" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 border-end" style={{ height: "125px", minWidth: "125px" }}>
              <i className="bi bi-card-heading fs-2 text-muted"></i>
            </div>
          )}
          <div className="p-2 flex-fill d-flex flex-column overflow-hidden text-light text-start">
            <div
              style={{
                fontSize: '110%',
                fontWeight: '600',
                color: '#e0e0e0', // light text color for title
              }}
              className="my-1 text-truncate"
            >
              {previewMeta.title}
            </div>
            <div
              className="socialcarddescription small"
            >
              {previewMeta.description}
            </div>
          </div>
        </div>
      )}
      <div className="position-absolute top-0 start-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-info">
          <i className="bi bi-share-fill me-1"></i>w3ly.io
        </div>
      </div>
      {association && currentUser && association?.transactor?.publicKey === currentUser?.PublicKeyBase58Check && (
        <div className="position-absolute top-0 end-0 m-2 rounded bg-black bg-opacity-75 d-flex flex-column w-auto">
          {association?.extraData?.linkType > 1 || association?.extraData?.upgraded ? (
            <>
              <Link to={`/edit/${association.associationId}`} className="text-decoration-none lh-1 small text-truncate text-light text-nowrap p-2 d-flex align-items-center bg-secondary rounded hover">
                <i className="bi bi-pen me-2"></i>Edit
              </Link>
              <Link to={`/analyse/${association.associationId}`} className="text-decoration-none lh-1 small text-truncate text-light text-nowrap p-2 d-flex align-items-center bg-secondary rounded hover">
                <i className="bi bi-graph-up me-2"></i>Analyse
              </Link>
            </>
          ) : (
            <span
              className="bg-secondary text-light text-nowrap d-flex align-items-center rounded hover p-2 small"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUpgradeModal(true); }}
            >
              <i className="bi bi-gem me-2"></i>Upgrade
            </span>
        )}
      </div>
    )}
    </Link>
    {/* Upgrade Link Modal */}
    {showUpgradeModal && (
      <UpgradeLink
        association={association}
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        setRefreshRequired={setRefreshRequired}
      />
    )}
    </div>
  );
}

export const getPlatformPreviews = (metadata, currentUser = null, association = null) => {
  if (!metadata) return [];

  const timeSince = friendlyFormatDate(new Date(metadata.datePublished));

  const previewBlock = <></>;
  // Facebook preview format
  const facebookPreview = (
    <div className="w-100 position-relative">
      {metadata.cardType === "summary_large_image" ? (
        <div className="position-relative bg-dark border border-muted rounded-3 overflow-hidden">
          <div
            className="ratio ratio-21x9 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${metadata.image})`,
              backgroundColor: "#333", // fallback in case the image doesn't load
            }}
          >
            <img
              className="d-block w-100 h-100 position-absolute top-0 object-fit-cover"
              src={metadata.image}
              alt="Preview"
            />
          </div>
          <div className="p-3 text-light text-start lh-1">
            <div
              className="text-uppercase small"
              style={{
                color: '#888', // softer gray for the site name
                fontFamily: `Helvetica, Arial, Sans-Serif`
              }}
            >
              W3LY.IO
            </div>
            <div
              style={{
                fontSize: '110%',
                fontWeight: '600',
                color: '#e0e0e0', // light text color for title
              }}
              className="my-1"
            >
              {metadata.title}
            </div>
            <div
              className="text-truncate"
              style={{
                fontSize: '14px',
                color: '#b0b0b0', // slightly muted color for description
                maxHeight: '80px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: `Helvetica, Arial, Sans-Serif`
              }}
            >
              {metadata.description}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-100 d-flex flex-row align-items-center position-relative bg-dark border border-muted rounded-3 overflow-hidden">
          {metadata.image ? (
            <Image 
              src={metadata.image} 
              className="object-fit-cover"
              style={{ height: "125px", width: "125px" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 border-end" style={{ height: "125px", minWidth: "125px" }}>
              <i className="bi bi-card-heading fs-2 text-muted"></i>
            </div>
          )}
          <div className="p-3 flex-fill d-flex flex-column overflow-hidden text-light text-start">
            <div
              className="text-uppercase small"
              style={{
                color: '#888', // softer gray for the site name
                fontFamily: `Helvetica, Arial, Sans-Serif`
              }}
            >
              W3LY.IO
            </div>
            <div
              style={{
                fontSize: '110%',
                fontWeight: '600',
                color: '#e0e0e0', // light text color for title
              }}
              className="my-1 text-truncate"
            >
              {metadata.title}
            </div>
            <div
              className="text-truncate"
              style={{
                fontSize: '14px',
                color: '#b0b0b0', // slightly muted color for description
                maxHeight: '80px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: `Helvetica, Arial, Sans-Serif`
              }}
            >
              {metadata.description}
            </div>
          </div>
        </div>
      )}
      <div className="position-absolute top-0 end-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-light">
          <i className="bi bi-facebook"></i>
        </div>
      </div>
    </div>
  );
  
  const linkedinPreview = (
    <div className="w-100 position-relative">
      <div className="position-relative bg-dark border border-muted rounded-1 shadow overflow-hidden">
        <div
          className="ratio ratio-linkedin bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${metadata.image})`,
            backgroundColor: "#333", // fallback in case the image doesn't load
          }}
        >
          <img
            className="d-block w-100 h-100 position-absolute top-0 object-fit-cover"
            src={metadata.image}
            alt="Preview"
          />
        </div>
        <div className="p-3 text-light text-start lh-1">
          <div
            className="text-truncate mb-1"
            style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#e0e0e0', // light text color for title
              lineHeight: '1em',
            }}
          >
            {metadata.title}
          </div>
          <div
            className="text-muted"
            style={{
              fontSize: '12px',
              lineHeight: '14px',
              color: '#888', // softer gray for the site name
            }}
          >
            w3ly.io
          </div>
        </div>
      </div>
      <div className="position-absolute top-0 end-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-light">
          <i className="bi bi-linkedin"></i>
        </div>
      </div>
    </div>
  );
  
  const discordPreview = (
    <div className="w-100 position-relative">

      <div className="position-relative bg-dark border-start border-3 discord rounded shadow-sm text-start">
        <div className="p-3 text-light">
          <div className="text-info fw-semibold fs-6 discord">{metadata.title}</div>
          <div className="mt-2 text-body-secondary small">{metadata.description}</div>
          {metadata.image && (
            <div className="mt-3">
              <img
                src={metadata.image}
                alt={metadata.title}
                className="img-fluid rounded w-50"
              />
            </div>
          )}
        </div>
      </div>

      <div className="position-absolute top-0 end-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-light">
          <i className="bi bi-discord"></i>
        </div>
      </div>
    </div>
  );

  const telegramPreview = (
    <div className="w-100 position-relative">
      {metadata.cardType === "summary_large_image" ? (
        <div className="position-relative bg-dark border-start border-4 border-warning-subtle rounded shadow-sm text-start">
          <div className="p-2 text-light">
            <div className="text-warning-emphasis small">w3ly.io</div>
            <div className="text-body fw-semibold">{metadata.title}</div>
            <div className="text-body-secondary">{metadata.description}</div>
            {metadata.image && (
              <div className="mt-3 ratio ratio-16x9">
                <img
                  src={metadata.image}
                  alt={metadata.title}
                  className="img-fluid object-fit-cover rounded"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="position-relative bg-dark border-start border-4 border-warning-subtle rounded shadow-sm text-start d-flex align-items-center">
          <div className="p-2 text-light flex-fill">
            <div className="text-warning-emphasis small">w3ly.io</div>
            <div className="text-body fw-semibold">{metadata.title}</div>
            <div className="text-body-secondary socialcarddescription">{metadata.description}</div>
          </div>
          {metadata.image && (
              <div className="m-2">
                <img
                  src={metadata.image}
                  alt={metadata.title}
                  className="img-fluid object-fit-cover rounded"
                  style={{ height: "125px", width: "125px", maxHeight: "125px", maxWidth: "125px" }}
                />
              </div>
            )}
        </div>
      )}
      <div className="position-absolute top-0 end-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-light">
          <i className="bi bi-telegram"></i>
        </div>
      </div>
    </div>
  );

  const xPreview = (
    <div className="w-100 position-relative">
      {metadata.cardType === "summary_large_image" ? (
        <>
          <div className="w-100 border rounded-4 shadow-sm border-body overflow-hidden position-relative">
            <div
              className="ratio ratio-x bg-dark"
              style={{
                backgroundImage: metadata.image ? `url(${metadata.image})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundColor: metadata.image ? "transparent" : "#333", // Fallback if no image
              }}
            />
            <div className="position-absolute bottom-0 start-0 m-2 p-2 rounded bg-black bg-opacity-75" style={{ maxWidth: "90%" }}>
              <div className="lh-1 small text-truncate text-light">
                {metadata.title}
              </div>
            </div>
          </div>
          <div className="mt-0 lh-1 text-start small text-muted">From w3ly.io</div>
        </>
      ) : (
        <div className="w-100 border rounded-4 shadow-sm border-secondary overflow-hidden position-relative d-flex align-items-stretch" style={{ maxHeight: "125px "}}>
          {metadata.image ? (
            <Image 
              src={metadata.image} 
              className="object-fit-cover"
              style={{ height: "125px", width: "125px" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 border-end" style={{ height: "125px", minWidth: "125px" }}>
              <i className="bi bi-card-heading fs-2 text-muted"></i>
            </div>
          )}
          <div className="flex-fill px-2 text-start d-flex flex-column overflow-hidden align-self-center small">
            <div className="small text-truncate text-muted">
              w3ly.io
            </div>
            <div className="fw-bold text-truncate">
              {metadata.title}
            </div>
            <div className="flex-grow-1 overflow-hidden socialcarddescription">
              {metadata.description}
            </div>
          </div>
        </div>
      )}
      <div className="position-absolute top-0 end-0 m-2 p-2 rounded bg-black bg-opacity-75">
        <div className="lh-1 small text-truncate text-light">
          <i className="bi bi-twitter-x"></i>
        </div>
      </div>
    </div>
  );

  return { 'twitter-x': xPreview, telegram: telegramPreview, facebook: facebookPreview, linkedin: linkedinPreview, discord: discordPreview };
};

const MetadataForm = ({ currentUser, metadata, setMetadata, resetMetadata }) => {
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setMetadata((prevMetadata) => ({
      ...prevMetadata,
      [name]: value,
      customised: "true"
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // You can handle image upload here, for now we just set the URL

      const imageUrl = await uploadImage({
        UserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        file: file,
      }, { nodeURI: 'https://node.deso.org' });

      if (imageUrl && imageUrl.ImageURL) {
        setMetadata((prevMetadata) => ({
          ...prevMetadata,
          image: imageUrl.ImageURL,
          customised: "true"
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Updated metadata:', metadata);
  };
  const fileInputRef = useRef(null);
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  return (
    <div className="container bg-secondary bg-opacity-10 rounded my-2 py-2 text-start">
      <div className="d-flex flex-row align-items-center justify-content-between mb-3">
        <span className="fw-bold">Customize Metadata</span>
        <span className="btn" onClick={resetMetadata}>
          <i className="bi bi-arrow-clockwise"></i>
        </span>
      </div>
      <Form onSubmit={handleSubmit}>
        {/* Card Type */}
        <InputGroup size="sm" className="w-100 mb-3">
          <InputGroup.Text>Card Type</InputGroup.Text>
          <Button
            variant={metadata.cardType === "summary_large_image" ? "primary" : "outline-secondary"}
            onClick={() => setMetadata((prevMetadata) => ({ ...prevMetadata, cardType: "summary_large_image", customised: "true" }))}
          >
            <i className="bi bi-card-image me-2"></i>
            Large
          </Button>
          <Button
            variant={metadata.cardType === "summary" ? "primary" : "outline-secondary"}
            onClick={() => setMetadata((prevMetadata) => ({ ...prevMetadata, cardType: "summary", customised: "true" }))}
          >
            <i className="bi bi-card-heading me-2"></i>
            Summary
          </Button>
        </InputGroup>
        
        <div className="form-floating mb-3">
          <Form.Control
            type="text"
            name="title"
            className={metadata.title ? `` : `is-invalid`}
            value={metadata.title}
            onChange={handleChange}
            placeholder="Enter title"
          />
          <Form.Label>Title</Form.Label>
        </div>

        {/* Description */}
        <div className="form-floating mb-3">
          <Form.Control
            as="textarea"
            name="description"
            className={metadata.description ? `` : `is-invalid`}
            value={metadata.description}
            onChange={handleChange}
            placeholder="Enter description"
            style={{ height: '150px' }}
          />
          <Form.Label>Description</Form.Label>
        </div>

        {/* Blog Article Toggle */}
        <Form.Group controlId="isBlog" className="mb-3">
          <Form.Check
            type="switch"
            id="isBlogSwitch"
            label="Blog Article"
            name="isBlog"
            checked={metadata.isBlog === "true"}
            onChange={(e) => handleChange({ target: { name: 'isBlog', value: e.target.checked ? "true" : "false" } })}
          />
        </Form.Group>

        <Form.Group controlId="image" className="mb-1">
          {metadata.image ? (
            <div className="position-relative overflow-hidden">
              <img className="img-fluid rounded" src={metadata.image} alt={`Image`} />
              <div className="position-absolute top-0 start-0 w-100 d-flex justify-content-end">
                <span
                    className="btn btn-sm btn-danger m-2"
                    onClick={() => {setMetadata((prevMetadata) => ({ ...prevMetadata, image: "", cardType: "summary"}))}}
                >
                    <i className="bi bi-trash-fill"></i>
                </span>
                <span
                    className="btn btn-sm btn-secondary m-2"
                    onClick={triggerFileInput}
                >
                    <i className="bi bi-paperclip"></i>
                </span>
              </div>
            </div>
          ) : (
            <Button variant="primary" className="mb-3" onClick={triggerFileInput}>
              <i className="bi bi-upload me-2"></i>
              Upload Image
            </Button>
          )}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
        </Form.Group>
      </Form>
    </div>
  );
};

export default MetadataForm;

const sanitizeToSlug = (text) => {
  // Convert to lowercase, remove non-alphanumeric characters (except - and _), and replace spaces with dashes
  return text
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')  // Replace non-alphanumeric with dash
    .replace(/^-+|-+$/g, '');        // Remove leading/trailing dashes
};

const LinkForm = ({ currentUser, formData, setFormData }) => {
  const linkOptions = [
    { value: 1, label: "Basic Link", example: "w3ly.io/Ex4mp1e", icon: "link", analytics: false, edit: false },
    { value: 2, label: "User", example: `w3ly.io/${currentUser?.ProfileEntryResponse?.Username}/Ex4mp1e`, icon: "person-fill text-primary", analytics: true, edit: true },
    { value: 3, label: "User Vanity", example: `w3ly.io/${currentUser?.ProfileEntryResponse?.Username}/custom`, icon: "star-fill text-danger", analytics: true, edit: true },
    { value: 4, label: "Premium Vanity", example: "w3ly.io/custom", icon: "fire text-warning", analytics: true, edit: true },
  ];

  return (
    <div className="mt-2 text-start">
      <Form>
        <span className="fw-bold">Link options</span>
        {linkOptions.map((option) => (
          <div
            key={option.value}
            className={`mt-2 ${formData.linkType === option.value ? "text-body fw-bold" : "text-muted"}`}
            onClick={() => setFormData({ ...formData, linkType: option.value })}
            style={{ cursor: "pointer" }}
          >
            <div className={`w-100 d-flex flex-row justify-content-start align-items-center text-start lh-1 p-2 ${formData.linkType === option.value ? `rounded bg-secondary bg-opacity-25 text-body` : `text-muted`}`}>
              <i className={`bi bi-${option.icon} me-2 fs-1 ps-1`}></i>
              <div className="flex-fill pe-1">
                <div>{option.label}</div>
                <div className="text-muted small fw-light">{option.example}</div>
              </div>
              {/*
              <div className="pe-3">
                {option.analytics ? <i className="bi bi-graph-up"></i> : <i className="bi bi-x opacity-50"></i>}
              </div>
              <div className="pe-1">
                {option.edit ? <i className="bi bi-pencil-square"></i> : <i className="bi bi-x opacity-50"></i>}
              </div>
              */}
            </div>
          </div>
        ))}

        {/* Custom Text Input (for User Custom and Premium options) */}
        {(formData.linkType === 3 || formData.linkType === 4) && (
          <>
          <Form.Group controlId="customText" className="mt-2">
            <div className="form-floating mb-3">
              <Form.Control
                type="text"
                value={formData.customText}
                className={formData?.slugError ? `is-invalid` : formData.customText && formData.customSlug ? `is-valid` : ``}
                onChange={(e) => setFormData({ ...formData, customText: e.target.value })}
                placeholder="Enter your custom url"
              />
              <Form.Label>Custom url</Form.Label>
            </div>
          </Form.Group>

          {formData.customSlug && (
            <Form.Control
              type="text"
              className={`mb-3 ${formData?.slugError ? `text-danger` : ``}`}
              value={ formData.linkType === 3 ? `/${currentUser.ProfileEntryResponse.Username}/${formData.customSlug}` : `/${formData.customSlug}` }
              disabled={true}
            />
          )}
          </>
        )}
      </Form>
    </div>
  );
};

/* fully working QR code generator with logo and color options 
const LinkQRCode = ({ data, currentUser, setQRParameters = undefined, preview = false }) => {
  const [tempQrColor, setTempQrColor] = useState("#FFFFFF");
  const [qrColor, setQrColor] = useState("#FFFFFF");

  const [isTransparent, setIsTransparent] = useState(true);
  const [tempQrBgColor, setTempQrBgColor] = useState("#000000");
  const [qrBgColor, setQrBgColor] = useState("#000000");

  const [tempQrAccentColor, setTempQrAccentColor] = useState("#EEEEEE");
  const [qrAccentColor, setQrAccentColor] = useState("#EEEEEE");

  const [tempQrStyle, setTempQrStyle] = useState("fluid");
  const [qrStyle, setQrStyle] = useState("fluid");

  const [tempEyeRadius, setTempEyeRadius] = useState(25);
  const [eyeRadius, setEyeRadius] = useState(25);

  const [tempLogoOpacity, setTempLogoOpacity] = useState(1);
  const [logoOpacity, setLogoOpacity] = useState(1);

  const [tempQuietZone, setTempQuietZone] = useState(10);
  const [quietZone, setQuietZone] = useState(10);

  const [logoBase64, setLogoBase64] = useState("");

  const qrRef = useRef(null);

  const size = 256;

  useEffect(() => {
    const fetchLogo = async () => {
      const response = await fetch(
        `https://node.deso.org/api/v0/get-single-profile-picture/${currentUser?.PublicKeyBase58Check}`,
        { mode: "cors" }
      );
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => setLogoBase64(reader.result);
      reader.readAsDataURL(blob);
    };
  
    if(currentUser?.PublicKeyBase58Check) {
      fetchLogo();
    }
  }, [currentUser?.PublicKeyBase58Check]);
  
  // Debounce effect (applies value after 300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setQrColor(tempQrColor);
      setQrBgColor(isTransparent ? "transparent" : tempQrBgColor);
      setQrAccentColor(tempQrAccentColor);
      setQrStyle(tempQrStyle);
      setEyeRadius(tempEyeRadius);
      setLogoOpacity(tempLogoOpacity);
      setQuietZone(tempQuietZone);
      if (setQRParameters) {
        setQRParameters({
          data,
          qrColor,
          qrBgColor,
          qrAccentColor,
          qrStyle,
          eyeRadius,
          logoOpacity,
          quietZone,
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    tempQrColor,
    tempQrAccentColor,
    tempQrStyle,
    tempEyeRadius,
    tempLogoOpacity,
    tempQuietZone,
    tempQrBgColor,
    isTransparent
  ]);

  const handleSaveQR = () => {
    if (qrRef.current && typeof qrRef.current.download === "function") {
      qrRef.current.download("png", "qrcode.png"); // Change "png" to another format if needed
    }
  };

  return (
    <div className="container text-center">
      <Row className="justify-content-center mt-3 align-items-center">
        <Col xs={12} md={5} className="my-1">
          <div className="ratio ratio-1x1">
            <QRCode
              ref={qrRef}
              value={data}
              size={size}
              style={{ width: "100%", maxWidth: "100%", height: "auto" }}
              fgColor={qrColor}
              bgColor={qrBgColor}
              logoImage={logoBase64}
              crossOrigin="anonymous" 
              logoPaddingStyle="circle"
              logoPadding={0}
              logoWidth={60}
              logoHeight={60}
              logoOpacity={logoOpacity}
              removeQrCodeBehindLogo={true}
              qrStyle={qrStyle}
              quietZone={quietZone}
              eyeRadius={eyeRadius}
              eyeColor={qrAccentColor}
            />
          </div>
          {preview && (
            <div className="text-muted small">
              <span className="fw-bold">Preview Only</span> - generate for actual QR code
            </div>
          )}
        </Col>
        <Col xs={12} md={7} className="align-items-center my-1 small">
          <Row>
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center mb-2 px-1">
              <span className="text-start my-0 fw-bold"><i className="bi bi-palette-fill me-2"></i>Palette</span>
            </Col>
            <Col xs={6} className="d-flex flex-row justify-content-between align-items-center mb-2 px-1">
              <Form.Label className="text-start my-0">Data</Form.Label>
              <Form.Control
                type="color"
                className="text-end form-control form-control-color"
                value={tempQrColor}
                onChange={(e) => setTempQrColor(e.target.value)}
              />
            </Col>
            <Col xs={6} className="d-flex flex-row justify-content-between align-items-center mb-2 px-1">
              <Form.Label className="text-start my-0 ps-1">Eyes</Form.Label>
              <Form.Control
                type="color"
                className="text-end form-control form-control-color"
                value={tempQrAccentColor}
                onChange={(e) => setTempQrAccentColor(e.target.value)}
              />
            </Col>
            <Col xs={6} className="d-flex flex-row justify-content-between align-items-center mb-2 px-1">
              <Form.Label className="text-start my-0">Background</Form.Label>
              <Form.Control
                type="color"
                className="text-end form-control form-control-color"
                value={tempQrBgColor}
                disabled={isTransparent}
                onChange={(e) => setTempQrBgColor(e.target.value)}
              />
            </Col>
            <Col xs={6} className="d-flex align-items-center justify-content-center mb-2 px-1">
              <Form.Check
                type="switch"
                className="mt-1"
                label="Transparent"
                checked={isTransparent}
                onChange={(e) => setIsTransparent(e.target.checked)}
              />
            </Col>
          </Row>

          <Row className="justify-content-center mt-2">
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center mb-2 px-1">
              <span className="text-start my-0 fw-bold"><i className="bi bi-qr-code me-2"></i>QR Style</span>
            </Col>
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center px-1">
              <Form.Label className="text-start my-0" style={{ width: "32%" }}>Data</Form.Label>
              <ButtonGroup className="flex-fill justify-content-center">
                {["square", "dots", "fluid"].map((style) => (
                  <ToggleButton
                    key={style}
                    type="radio"
                    variant="outline-secondary"
                    size="sm"
                    name="qrStyle"
                    value={style}
                    checked={tempQrStyle === style}
                    onClick={() => setTempQrStyle(style)}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Col>
          </Row>

          <Row className="justify-content-center mt-2">
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center mb-2 px-1">
              <div className="w-100 d-flex flex-row align-items-center">
                <Form.Label className="text-start mb-0" style={{ width: "32%" }}>Eye Radius</Form.Label>
                <Form.Range
                  min={0}
                  step={1}
                  max={(25)}
                  value={tempEyeRadius}
                  className=""
                  style={{ width: "53%" }}
                  onChange={(e) => setTempEyeRadius(Number(e.target.value))}
                />
                <span className="text-end" style={{ width: "15%" }}>{(tempEyeRadius/(25)*100).toFixed(0)}%</span>
              </div>
            </Col>
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center mb-2 px-1">
              <div className="w-100 d-flex flex-row align-items-center">
                <Form.Label className="text-start mb-0" style={{ width: "32%" }}>Avatar Visibility</Form.Label>
                <Form.Range
                  min={0}
                  max={1}
                  step={0.05}
                  value={tempLogoOpacity}
                  style={{ width: "53%" }}
                  onChange={(e) => setTempLogoOpacity(Number(e.target.value))}
                />
                <span className="text-end" style={{ width: "15%" }}>{(tempLogoOpacity*100).toFixed(0)}%</span>
              </div>
            </Col>
            <Col xs={12} className="d-flex flex-row justify-content-start align-items-center px-1">
              <div className="w-100 d-flex flex-row align-items-center">
                <Form.Label className="text-start mb-0" style={{ width: "32%" }}>Padding</Form.Label>
                <Form.Range
                  min={0}
                  step={1}
                  max={(40)}
                  value={tempQuietZone}
                  style={{ width: "53%" }}
                  onChange={(e) => setTempQuietZone(Number(e.target.value))}
                />
                <span className="text-end" style={{ width: "15%" }}>{(tempQuietZone/(40)*100).toFixed(0)}%</span>
              </div>
            </Col>
          </Row>
          {!preview && (
            <Button className="mt-3" onClick={handleSaveQR}>
              <i className="bi bi-floppy-fill me-2" /> Save QR Code
            </Button>
          )}
        </Col>
      </Row>
    </div>
  );
};
*/


export const UpgradeLink = ({ association, showUpgradeModal, setShowUpgradeModal, onClose, setRefreshRequired }) => {
  const { currentUser } = useContext(DeSoIdentityContext);
  const { preferences } = useUserPreferences();
  const { exchangeRates } = useContext(ExchangeRatesContext);

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => setShowUpgradeModal(false);
  const handleShow = () => setShowUpgradeModal(true);

  let feeUSD = 0.25;
  let feeInDESONanos = 25000000;
  
  if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
    feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
  }

  const upgradeShortLink = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    setLoading(true);
    
    try {
      const associationRequest = {
        AppPublicKeyBase58Check: systemKey,
        TargetUserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationType: "SHORTLINK",
        AssociationValue: association.associationValue,
        ExtraData: {
          ...association.extraData,
          upgraded: "true",
        },
        TransactionFees: [localFees(null, feeInDESONanos)],
      };

      console.log("Upgrade ShortLink:", associationRequest);
      const associationResponse = await createUserAssociation(associationRequest);
      console.log("Upgrade ShortLink Response:", associationResponse);

      if (associationResponse?.submittedTransactionResponse) {
        setSuccessMessage("Short Link Upgraded Successfully!");
      } else {
        setErrorMessage("Unable to Upgrade Short Link, please try again.");
      }
    } catch (error) {
      console.error("Error upgrading short link:", error);
      setErrorMessage("Unable to Upgrade Short Link, please try again.");
    }

    setRefreshRequired(true);
    setLoading(false);
  };

  return (
    <>
      {/* Modal */}
      <Modal show={showUpgradeModal} onHide={handleClose} centered>
        <Modal.Body className="text-center">
          <h4 className="mb-3"><i className="bi bi-gem text-info me-2"></i>Upgrade Link?</h4>

          <p>Upgrade this link to change metadata and analyse performance.</p>
          
          {/* Success or error messages */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

          <div className={`mt-3 d-flex flex-row align-items-center justify-content-between`}>
            {!successMessage ? (
              <>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                  Close
                </Button>
                <Button variant="success" onClick={upgradeShortLink} disabled={loading}>
                  {loading ? "Upgrading..." : <>Confirm Upgrade {localCurrency(feeInDESONanos/1e9,preferences,exchangeRates)}</>}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                  Close
                </Button>
                <Link to={`/analyse/${association.associationId}`} className="btn btn-success">
                  Analyse Link
                </Link>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
const sendStats = async (association, type = 'retrieved', currentUser = null) => {
  if(!association) return;
  const userLocale = new Intl.Locale(navigator.language);
  const regionCode = userLocale.region || "Unknown";
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown";
  const estimatedCountry = regionCode !== "Unknown" 
  ? new Intl.DisplayNames(["en"], { type: "region" }).of(regionCode) 
  : timeZone.split("/")[0] !== "Unknown" 
    ? new Intl.DisplayNames(["en"], { type: "region" }).of(timeZone.split("/")[0]) 
    : "Unknown";

  // Fetch battery level and charging status
  const batteryStatus = await navigator.getBattery?.();
  const batteryLevel = batteryStatus ? batteryStatus.level * 100 + "%" : "Unknown";
  const isCharging = batteryStatus ? batteryStatus.charging : false;

  const trackingData = {
    associationId: association.associationValue,
    type: type,
    user: currentUser?.PublicKeyBase58Check || null, 
    timestamp: new Date().toISOString(),
    referrer: document.referrer || "Direct",
    deviceType: /Mobi|Android/i.test(navigator.userAgent) ? "Mobile" : "Desktop",
    browser: navigator.userAgent.match(/(firefox|msie|chrome|safari|edge)/i)?.[0] || "Unknown",
    os: navigator.userAgent.match(/(Windows|Mac|Linux|Android|iOS)/i)?.[0] || "Unknown",
    country: estimatedCountry,
    timeZone: timeZone,
    timeZoneOffset: new Date().getTimezoneOffset(),
    language: navigator.language || "Unknown",
    darkMode: window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "Dark" : "Light",
    screenSize: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth || "Unknown",
    cpuCores: navigator.hardwareConcurrency || "Unknown", // Logical processor cores
    memoryGB: navigator.deviceMemory || "Unknown", // Captures memory (GB), but may not be accurate for larger systems like 48 GB
    connectionType: navigator.connection?.effectiveType || "Unknown",
    connectionSpeed: navigator.connection?.downlink || "Unknown", // Connection speed in Mbps
    inputMethod: 'ontouchstart' in window ? "Touch" : "Mouse",
    cookiesEnabled: navigator.cookieEnabled ? "Yes" : "No",
    jsEnabled: "Yes", // This script wouldn't run otherwise.
    indexedDB: "indexedDB" in window ? "Yes" : "No",
    serviceWorker: "serviceWorker" in navigator ? "Yes" : "No",
    batteryLevel: batteryLevel, // Await required for battery level
    isCharging: isCharging ? "Yes" : "No", // Mains powered check
    gpu: (() => {
      try {
        const canvas = document.createElement("canvas");
        const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
        return gl?.getParameter(gl?.VERSION) || "Unknown";
      } catch {
        return "Unknown";
      }
    })()
  };

  console.log("Tracking Data:", trackingData);

  await fetch("https://beyondsocial.app/api.php", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint: 'w3ly_tracking', data: trackingData }),
  });
};


export const CreateQRCode = ({ preview = false, suppliedPostHashHex, association }) => {
  const { currentUser, alternateUsers, isLoading } = useContext(DeSoIdentityContext);
  const { preferences } = useUserPreferences();
  const mode = association?.associationId ? true : false;
  const [input, setInput] = useState(association?.extraData?.value || "");
  const [copied, setCopied] = useState(false);
  const [extraData, setExtraData] = useState(association?.extraData || null);
  const [type, setType] = useState(association?.extraData?.type || '');
  const [generated, setGenerated] = useState(association ? true : false);
  const [showPreview, setShowPreview] = useState('twitter-x');

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const [formData, setFormData] = useState({});
  const { exchangeRates } = useContext(ExchangeRatesContext);
  let debounceTimer = null;

  let feeUSD = 0.05;
  let feeInDESONanos = 25000000;
  let updateFeeUSD = 0.05;
  let updateFeeInDESONanos = 5000000;
  
  if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
    feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
    updateFeeInDESONanos = Math.round((updateFeeUSD / exchangeRates["USD"]["rate"]) * 1e9);
  }

  useEffect(() => {
    feeUSD = 0.25 * formData.linkType;
    if (exchangeRates && exchangeRates["USD"] && exchangeRates["USD"]["rate"]) {
      feeInDESONanos = Math.round((feeUSD / exchangeRates["USD"]["rate"]) * 1e9);
    }
    console.log(formData,feeUSD,feeInDESONanos);
  }, [formData.linkType, exchangeRates])

  useEffect(() => {
    console.log("formData",formData,"extraData",extraData);
  }, [formData,extraData]);
  
  const handleInputChange = (e) => {
    if(type === 'url') {
      const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,}))([\/\?%#&=0-9a-zA-Z_-]*)$/;
      let isValidInput = urlPattern.test(e.target.value) || false;
      setFormData({ ...formData, invalidURL: !isValidInput ? 'Enter a valid URL' : null });
    } else {
      setFormData({ ...formData, invalidURL: null });
    }
    setInput(e.target.value);
  };
  
  const createQRCode = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      let guid = nanoid(8);
      let shortId = guid;

      const { value, ...qrParametersWithoutValue } = extraData;
      const qrParameters = {
        ...qrParametersWithoutValue,
        value: value || input,
      }

      const associationRequest = {
        AppPublicKeyBase58Check: systemKey,
        TargetUserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationType: "QRCODE",
        AssociationValue: shortId,
        ExtraData: {
          qrParameters: JSON.stringify(qrParameters),
        },
        TransactionFees: [localFees(null, feeInDESONanos)],
      };
      
      console.log("Create Association:",associationRequest);
      const associationResponse = await createUserAssociation(associationRequest);
      console.log("Association:",associationRequest,associationResponse);

      // Step 3: Check if the creation was successful and update the UI
      if (associationResponse?.submittedTransactionResponse) {
        setGenerated(true);
        setSuccessMessage("Short Link Created");
      } else {
        console.error("Failed to create short link on blockchain:", associationResponse?.error);
        setErrorMessage(`Unable to ${association ? `update` : `create`} Short Link, please try again`);
      }
    } catch (error) {
      console.error("Error creating short link:", error);
      setErrorMessage(`Unable to ${association ? `update` : `create`} Short Link, please try again`);
    }
  };

  const deleteAssociation = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    if(!association) return;

    try {
      const associationRequest = {
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationID: association.associationId,
        TransactionFees: [localFees()],
      };
      
      console.log("Delete Association:",associationRequest);
      const associationResponse = await deleteUserAssociation(associationRequest);
      console.log("Association:",associationRequest,associationResponse);

      // Step 3: Check if the creation was successful and update the UI
      if (associationResponse?.submittedTransactionResponse) {
        setGenerated(false);
        setSuccessMessage("Short Link Deleted")
      } else {
        console.error("Failed to delete short link on blockchain:", associationResponse?.error);
        setErrorMessage("Unable to delete Short Link, please try again");
      }
    } catch (error) {
      console.error("Error deleting short link:", error);
      setErrorMessage("Unable to delete Short Link, please try again");
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'undefined',
        url: input,
      }).catch((error) => {
        console.error('Error sharing:', error);
      });
    } else {
      alert('Sorry, sharing is not supported on this device - please copy the link instead.');
    }
  };
  
  const types = {
    'message': {
      label: "Message",
      icon: "bi-chat-left-text",
      placeholder: "Enter a message...",
    },
    'wallet': {
      label: "Wallet Address",
      icon: "bi-key",
      placeholder: currentUser?.PublicKeyBase58Check,
    },
    'url': {
      label: "URL",
      icon: "bi-link",
      placeholder: "Enter URL...",
    },
  }

  return (
      <div className="card p-3">
        {preview ? (
          <h6 className="mb-3">Example</h6>
        ) : (
          <div>
            {mode === "edit" ? (
              <h6>Edit your link</h6>
            ) : (
              <h6>What type of QR code would you like to create?</h6>
            )}
            <ButtonGroup className="flex-fill justify-content-center">
              {Object.entries(types).map(([key, { label, icon }]) => (
                <ToggleButton
                  key={key}
                  type="radio"
                  variant="outline-secondary"
                  name="qrStyle"
                  value={key}
                  checked={type === key}
                  onClick={() => setType(key)} // or onClick if you prefer
                  className={type === key ? 'active' : ''}
                >
                  <i className={`bi ${icon} me-1`} /><span className="text-nowrap">{label}</span>
                </ToggleButton>
              ))}
            </ButtonGroup>

            {type && (
              <>
                <input
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  placeholder={types[type]?.placeholder}
                  className={`form-control mt-3 ${formData.invalidURL ? `is-invalid` : ``}`}
                  disabled={preview}
                />
                {formData?.invalidURL && (<div className="text-danger mt-2">{formData.invalidURL}</div>)}
              </>
            )}
          </div>
        )}
        {generated ? (
          <>
            <LinkQRCode data={input} currentUser={currentUser} />
          </>
        ) : (
          <>
            {type && (
              <>
                <LinkQRCode currentUser={currentUser} setQRParameters={setExtraData} preview={true} />
                <div className="mt-3 d-flex flex-row align-items-center justify-content-center">
                  <Button variant="success" onClick={createQRCode} disabled={preview}>
                    {preview ? "Generate QR Code" : <>Create QR Code {localCurrency(feeInDESONanos/1e9,preferences,exchangeRates)}</>}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
};